import { UserPermissions } from 'applicaiton/constants/userPermissions';
import { UserRoles } from 'applicaiton/constants/userRoles';
import {
  getUserProPermissions,
  getUserPermissions,
  getUserRole,
} from 'applicaiton/sessionStorage/auth';
import { userRoleExactMatch } from './userRoleExactMatch';

/**
 * Checks if a user has a specific permission based on their role and permissions.
 *
 * @param type - The permission type to check for.
 * @param clinicOwnerId - (Optional) The ID of the clinic owner to check permissions for.
 * @param checkCOProPermission - (Optional) Whether or not to check CO Pro permissions.
 * @returns True if the user has the specified permission, false otherwise.
 */
export const userHasPermission = (
  type: UserPermissions,
  clinicOwnerId?: number,
  checkCOProPermission?: boolean,
): boolean => {
  const userRole = getUserRole();
  const permissions = getUserPermissions();
  const proPermissions = getUserProPermissions();
  const isClinicOwner = userRole?.includes(UserRoles.clinicOwner);
  const isProfessional = userRole?.includes(UserRoles.professional);
  const isAdmin = userRoleExactMatch(UserRoles.admin);
  const isSecretary = userRole?.includes(UserRoles.secretary);

  // If the user is a clinic owner and we don't need to check CO Pro permissions, they have the permission.
  if (isClinicOwner && !checkCOProPermission) {
    return true;
  }

  // If the user has pro permissions and is a professional, we need to check their specific permissions for the given clinic owner ID.
  if (proPermissions?.length && isProfessional) {
    if (clinicOwnerId) {
      // Find the permissions object that contains an entry for the given clinic owner ID.
      const permissionsByClinicOwnerId = proPermissions.find(
        (item: any) => !!item[clinicOwnerId],
      );

      // If there's no entry for this clinic owner ID, they don't have the permission.
      if (!permissionsByClinicOwnerId) return false;

      // Check if the given permission type is included in their permissions for this clinic owner ID.
      return permissionsByClinicOwnerId[clinicOwnerId]?.includes(type);
    }
    // If there's no specific clinic owner ID provided, we need to check all of the user's pro permissions
    // to see if they have the given permission type anywhere.
    const hasPermission = proPermissions.some((item: any) => {
      for (const property in item) {
        return item[property].includes(type);
      }
    });
    return hasPermission;
  }

  // If the user is an admin or a secretary, they automatically have all permissions.
  if (isAdmin || isSecretary) {
    return !!permissions?.includes(type);
  }

  // Otherwise, the user does not have the given permission.
  return false;
};
